import type { AuthUserDto, OrgInviteDto, OrgMemberDto } from '../generated/contracts';
import { signOut } from '../core/AccountGate';
import {
  createOrgInvite,
  getOrgInvites,
  getOrgMembers,
  NotSignedInError,
  removeOrgMember,
  revokeOrgInvite,
  updateOrgMemberDisplayName,
} from '../net/api';
import { el, replace } from './portal/dom';
import { initialsOf, plural, relativeTime } from './portal/format';
import { card, cardHead, pageHead, PortalShell, type PortalShellNav } from './portal/shell';

/**
 * The org owner's one page for managing who is in the org: the people, and the invites that let
 * somebody become one.
 *
 * ================================================================================
 * NOTHING IS EMAILED, AND THIS PAGE MUST NEVER IMPLY OTHERWISE.
 * ================================================================================
 *
 * `OrgInvite` has no delivery state, no "sent" timestamp and no resend action, because there is
 * no SMTP anywhere in this product and none is planned. The owner's own decision, verbatim:
 * *"It wouldn't be an allow list, it's more of an invite that gets realized at the first login.
 * Just no email, I'll ping them myself."* So recording an invite writes a row and stops, and this
 * page's job at that moment is to make the address **easy to hand over**: it is shown back at
 * full size with a copy control beside it, because passing it on is the next thing that actually
 * has to happen and it is a person who has to do it.
 *
 * **The Google test-user step is on the page, as a step rather than an explanation.**
 * While the shared OAuth app stays in Testing publishing status — the owner's own deliberate
 * choice — Google refuses the consent screen outright for any address not separately added as a
 * test user in its console, and that refusal happens at the provider with nothing in this
 * server's logs to explain it. So an invite row admits nobody on its own. That is a real
 * constraint and it has to be visible, but plan/portal-design.md's second rule means it cannot be
 * a paragraph. It is two imperative fragments in a "Still to do" list next to the address, both
 * under ten words: things to do, not facts about why. The server sends the same two on the wire
 * (`OrgInviteCreatedDto.nextSteps`) at full length; those are the operator's record, not this
 * page's copy.
 *
 * **Remove from org, not archive the account.** The control removes someone from *this* org —
 * one owner's blast radius is already exactly their own org, since a user belongs to exactly one
 * (`plan/session-exit-and-membership-fixes.md`, "B. Remove from org, not archive the account") —
 * and it says so rather than reading as a global account deactivation. There is no Archived tab
 * and no Restore button: removal is confirmed up front (the person loses access, and the
 * confirmation says they can be invited back), and the way back in afterwards is a fresh invite,
 * the same as admitting anybody else. A removed row simply leaves this list; the server never
 * hands the client an "archived" state to render or filter.
 *
 * **Owner-only, and the server is what enforces it.** Every route this page calls answers a 403
 * to a plain member. This page is only reachable from the rail when the overview said
 * `isOwner`, and that is presentation rather than protection: a page that routinely fires a
 * request it knows will be refused teaches the next reader that the refusal is routine.
 *
 * **Renaming a colleague is a row-level control, not a confirmation dialog.** Unlike removal —
 * which loses somebody access and so is confirmed up front — a rename is reversible by typing it
 * again, so it gets the same inline "Rename" / edit-in-place shape the portal's own rail-foot
 * uses for renaming yourself (`portal/shell.ts`), rather than a browser prompt. The owner's own
 * name is edited through that rail control, not through this table: this page's rows are always
 * somebody *else's*, since the caller does not list themselves as a row to act on.
 */
export interface PortalPeopleDeps {
  getOrgMembers: typeof getOrgMembers;
  getOrgInvites: () => Promise<OrgInviteDto[]>;
  createOrgInvite: typeof createOrgInvite;
  revokeOrgInvite: typeof revokeOrgInvite;
  removeOrgMember: typeof removeOrgMember;
  updateOrgMemberDisplayName: typeof updateOrgMemberDisplayName;

  /**
   * Browser `confirm`, injected so a test can drive both answers without a real dialog. Returning
   * `false` (the user chose Cancel, or a test stubs it that way) leaves the member exactly where
   * they were — nothing is sent to the server.
   */
  confirm: (message: string) => boolean;
}

const defaultDeps: PortalPeopleDeps = {
  getOrgMembers,
  getOrgInvites,
  createOrgInvite,
  revokeOrgInvite,
  removeOrgMember,
  updateOrgMemberDisplayName,
  confirm: (message) => window.confirm(message),
};

export class PortalPeople {
  private readonly shell: PortalShell;
  private readonly deps: PortalPeopleDeps;
  private readonly now: Date;

  private members: OrgMemberDto[] = [];
  private invites: OrgInviteDto[] = [];

  /** The invite just recorded, if any — the address to hand over, kept until the next reload. */
  private justInvited: string | null = null;

  /** The member whose row is currently showing the rename form, if any — at most one at a time. */
  private renamingEmail: string | null = null;

  constructor(
    host: HTMLElement,
    account: AuthUserDto,
    private readonly onBack: () => void,
    deps: Partial<PortalPeopleDeps> = {},
    now: Date = new Date(),
  ) {
    this.deps = { ...defaultDeps, ...deps };
    this.now = now;

    const nav: PortalShellNav = {
      go: (route) => {
        if (route === '/portal') {
          this.onBack();
        }
      },
      enterRoom: null,
    };

    // `true` for the People rail item: this page is only ever reached by an owner, so there is
    // no overview response to wait for before knowing the destination belongs in the rail.
    this.shell = new PortalShell(host, account, '/portal/people', nav, null, true);

    replace(this.shell.main, pageHead('People'));

    void this.load();
  }

  dispose(): void {
    this.shell.dispose();
  }

  private async load(): Promise<void> {
    // Both lists, in parallel, and each failure surfaced in its own card: an invite list that
    // will not load must not take the member table with it.
    const [members, invites] = await Promise.allSettled([
      this.deps.getOrgMembers(),
      this.deps.getOrgInvites(),
    ]);

    if (members.status === 'rejected' && members.reason instanceof NotSignedInError) {
      void signOut();
      return;
    }

    this.members = members.status === 'fulfilled' ? members.value : [];
    this.invites = invites.status === 'fulfilled' ? invites.value : [];

    this.render(
      members.status === 'rejected' ? describe(members.reason, "Couldn't load the people in your org.") : null,
      invites.status === 'rejected' ? describe(invites.reason, "Couldn't load your invites.") : null,
    );
  }

  // ---------------------------------------------------------------- rendering

  private render(membersError: string | null = null, invitesError: string | null = null): void {
    replace(
      this.shell.main,
      pageHead('People'),
      this.peopleCard(membersError),
      this.invitesCard(invitesError),
    );
  }

  private peopleCard(error: string | null): HTMLElement {
    const body = error
      ? el('div', { class: 'empty', text: error })
      : el('div', { class: 'table-scroll' }, [
          el('table', {}, [
            el('thead', {}, [
              el('tr', {}, [column('Person'), column('Role'), column('Last seen'), column('')]),
            ]),
            this.members.length === 0
              ? el('tbody', {}, [
                  el('tr', {}, [
                    el('td', { colspan: '4', class: 'empty', text: 'Nobody here yet.' }),
                  ]),
                ])
              : el('tbody', {}, this.members.map((member) => this.memberRow(member))),
          ]),
        ]);

    return card([
      cardHead('People', `${plural(this.members.length, 'person', 'people')} in your org`),
      body,
    ]);
  }

  private memberRow(member: OrgMemberDto): HTMLElement {
    const renaming = this.renamingEmail === member.email;

    const remove = el('button', {
      type: 'button',
      class: 'btn',
      'data-remove': member.email,
      text: 'Remove from org',
    });

    remove.addEventListener('click', () => void this.remove(member, remove));

    const rename = el('button', {
      type: 'button',
      class: 'btn',
      'data-rename': member.email,
      text: 'Rename',
    });

    rename.addEventListener('click', () => {
      this.renamingEmail = member.email;
      this.render();
    });

    return el('tr', {}, [
      el('td', {}, [
        renaming
          ? this.renameForm(member)
          : el('div', { class: 'person' }, [
              el('span', {
                class: 'initials',
                'aria-hidden': 'true',
                text: initialsOf(member.displayName, member.email),
              }),
              el('span', { class: 'person-name' }, [
                el('strong', { text: member.displayName || member.email }),
                el('span', { text: member.email }),
              ]),
            ]),
      ]),
      el('td', {}, [
        el('span', { class: 'pill', 'data-status': member.role === 'owner' ? 'ok' : null, text: member.role }),
      ]),
      el('td', { class: 'muted', text: relativeTime(member.lastSeenAt ?? null, this.now) }),
      el('td', {}, [renaming ? null : rename, renaming ? null : remove]),
    ]);
  }

  private renameForm(member: OrgMemberDto): HTMLElement {
    const input = el('input', {
      type: 'text',
      class: 'field',
      'data-rename-input': member.email,
      value: member.displayName,
      maxlength: '100',
      'aria-label': `Display name for ${member.email}`,
    });

    const save = el('button', { type: 'submit', class: 'btn btn-primary', 'data-rename-save': member.email, text: 'Save' });
    const cancel = el('button', { type: 'button', class: 'btn', 'data-rename-cancel': member.email, text: 'Cancel' });

    cancel.addEventListener('click', () => {
      this.renamingEmail = null;
      this.render();
    });

    const form = el('form', { class: 'row', 'data-rename-form': member.email }, [input, save, cancel]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitRename(member, input, save);
    });

    return form;
  }

  private async submitRename(member: OrgMemberDto, input: HTMLInputElement, submit: HTMLButtonElement): Promise<void> {
    const name = input.value.trim();

    if (name.length === 0) {
      return;
    }

    submit.disabled = true;

    try {
      const updated = await this.deps.updateOrgMemberDisplayName(member.email, name);

      this.members = this.members.map((m) => (m.email === updated.email ? updated : m));
      this.renamingEmail = null;
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      submit.disabled = false;
      submit.after(el('span', { class: 'muted', text: describe(error, "Couldn't save that name.") }));
    }
  }

  /**
   * Confirmed up front, in the browser's own dialog: nothing is sent to the server until the
   * owner accepts it. The message says what happens and that it is reversible, and nothing about
   * the schema behind it — plan/portal-design.md's second rule, applied to a confirmation prompt
   * exactly as much as to any other copy on this page.
   */
  private async remove(member: OrgMemberDto, button: HTMLButtonElement): Promise<void> {
    const name = member.displayName || member.email;
    const confirmed = this.deps.confirm(
      `Remove ${name} from the org? They will lose access, and can be invited back later.`,
    );

    if (!confirmed) {
      return;
    }

    button.disabled = true;

    try {
      await this.deps.removeOrgMember(member.email);

      this.members = this.members.filter((m) => m.email !== member.email);
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      button.disabled = false;

      // The server's own `detail` says which refusal this was — removing yourself, or removing
      // the system account — and both are things an owner can act on, so it is shown rather than
      // replaced with a generic message.
      button.after(el('span', { class: 'muted', text: describe(error, "Couldn't remove that person.") }));
    }
  }

  private invitesCard(error: string | null): HTMLElement {
    const body = error
      ? el('div', { class: 'empty', text: error })
      : el('div', { class: 'table-scroll' }, [
          el('table', {}, [
            el('thead', {}, [
              el('tr', {}, [column('Address'), column('Status'), column('Invited'), column('')]),
            ]),
            this.invites.length === 0
              ? el('tbody', {}, [
                  el('tr', {}, [el('td', { colspan: '4', class: 'empty', text: 'No invites yet.' })]),
                ])
              : el('tbody', {}, this.invites.map((invite) => this.inviteRow(invite))),
          ]),
        ]);

    return card([
      cardHead('Invites', `${plural(this.invites.filter((i) => i.status === 'pending').length, 'pending invite')}`),
      this.inviteForm(),
      this.justInvited === null ? null : this.handOver(this.justInvited),
      body,
    ]);
  }

  private inviteForm(): HTMLElement {
    const input = el('input', {
      type: 'email',
      class: 'field',
      'data-invite-email': 'true',
      placeholder: 'name@company.com',
      maxlength: '320',
      'aria-label': 'Email address to invite',
    });

    const submit = el('button', {
      type: 'submit',
      class: 'btn btn-primary',
      'data-invite-submit': 'true',
      text: 'Invite',
    });

    const form = el('form', { class: 'table-tools', 'data-invite-form': 'true' }, [
      el('div', { class: 'row' }, [input, submit]),
    ]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitInvite(input, submit);
    });

    return form;
  }

  /**
   * The address, at full size, with a copy control — and the two things the product could not do,
   * as a short list of actions.
   *
   * This is the shortest true form of a genuinely awkward constraint. An invite row admits
   * nobody until the same address is also a Google console test user, and Google's refusal
   * happens before this server sees the attempt. Saying so as prose would be exactly the
   * schema-explaining caption plan/portal-design.md's second rule removed everywhere else. Saying
   * nothing would leave an owner watching an invited colleague be refused with no way to find
   * out why. Two imperative fragments is what is left when both of those are ruled out.
   */
  private handOver(email: string): HTMLElement {
    const copy = el('button', { type: 'button', class: 'btn', 'data-copy-invite': 'true', text: 'Copy' });

    copy.addEventListener('click', () => {
      // Best-effort: the Clipboard API needs a secure context and a permission this page may not
      // have. The address is on screen at full size regardless, which is the part that matters —
      // a failed copy must not look like a failed invite.
      void navigator.clipboard?.writeText(email).then(
        () => {
          copy.textContent = 'Copied';
        },
        () => {
          copy.textContent = 'Select it';
        },
      );
    });

    return el('div', { class: 'invite-handover', 'data-invite-handover': 'true' }, [
      el('div', { class: 'row' }, [el('strong', { class: 'mono invite-address', text: email }), copy]),
      el('ul', { class: 'invite-steps' }, [
        el('li', { text: 'Send them this address yourself' }),
        el('li', { text: 'Add it to the Google console test users' }),
      ]),
    ]);
  }

  private async submitInvite(input: HTMLInputElement, submit: HTMLButtonElement): Promise<void> {
    const email = input.value.trim();

    if (email.length === 0) {
      return;
    }

    submit.disabled = true;

    try {
      const created = await this.deps.createOrgInvite(email);

      input.value = '';
      this.justInvited = created.invite.email;
      // DE-DUPED ON THE ADDRESS, NOT ON THE ID, because the address is the server's own
      // invariant: `(OrgId, Email)` is unique, and re-inviting a revoked address REOPENS the
      // existing row rather than inserting a second one. Matching on id looked equivalent and is
      // strictly weaker — it holds only while the server keeps the same id through a reopen, and
      // it produced a visible double row the moment a response carried a different one.
      this.invites = [
        created.invite,
        ...this.invites.filter(
          (i) => i.email.toLowerCase() !== created.invite.email.toLowerCase(),
        ),
      ];
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      submit.disabled = false;

      // The server distinguishes "already invited", "already joined" and "not an email address",
      // and all three are things the person typing can act on.
      submit.after(el('span', { class: 'muted', text: describe(error, "Couldn't record that invite.") }));
    }
  }

  private inviteRow(invite: OrgInviteDto): HTMLElement {
    const pending = invite.status === 'pending';

    const revoke = el('button', {
      type: 'button',
      class: 'btn',
      'data-revoke': invite.id,
      text: 'Revoke',
    });

    revoke.addEventListener('click', () => void this.revoke(invite, revoke));

    return el('tr', {}, [
      el('td', {}, [el('span', { class: 'mono', text: invite.email })]),
      el('td', {}, [
        el('span', {
          class: 'pill',
          'data-status': invite.status === 'accepted' ? 'ok' : invite.status === 'revoked' ? 'warn' : null,
          text: invite.status,
        }),
        // The account an accepted invite admitted is NOT printed beside the status any more.
        // It used to read "accepted · kari" — a derived username, which was the one thing on this
        // page a person had never chosen. Now that an admitted account carries the invited
        // address verbatim, printing it here would repeat the Address column one cell to the
        // left, character for character. `acceptedUsername` stays on the wire as the audit
        // record of who was admitted; the page has nothing to add to what it already shows.
      ]),
      el('td', { class: 'muted', text: relativeTime(invite.createdAt, this.now) }),

      // REVOKE IS ONLY OFFERED ON A PENDING INVITE, and the omission is the honest half of the
      // server's own 409. Revoking an accepted invite does nothing — the person is a member, and
      // an invite is not what grants their access — so a live-looking control here would let an
      // owner believe they had cut off access they had not touched.
      el('td', {}, [pending ? revoke : el('span', { class: 'muted', text: '—' })]),
    ]);
  }

  private async revoke(invite: OrgInviteDto, button: HTMLButtonElement): Promise<void> {
    button.disabled = true;

    try {
      const updated = await this.deps.revokeOrgInvite(invite.id);

      this.invites = this.invites.map((i) => (i.id === updated.id ? updated : i));
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      button.disabled = false;
      button.after(el('span', { class: 'muted', text: describe(error, "Couldn't revoke that invite.") }));
    }
  }
}

function column(label: string): HTMLElement {
  return el('th', { scope: 'col' }, [el('span', { class: 'col', text: label })]);
}

function describe(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}
